import { type ReactNode, useCallback, useEffect, useId, useState } from 'react'

import {
  type ChangedConnection,
  type Connection,
  type ConnectionField,
  type NewConnection,
  type SyncRun,
  type System,
  type TimetableElement,
  addConnection,
  changeConnection,
  chooseTimetable,
  fetchExternalAccess,
  fieldRefused,
  removeConnection,
  suggestedTenant,
  sync,
  testConnection,
  whoseTimetableFor,
} from '../api/connections'
import { asFailure, type Failure } from '../api/request'
import { useLoad } from '../api/useLoad'
import { Lanes } from '../frame/Lanes'
import { useAnnounce } from '../ui/Announce'
import { Button } from '../ui/Control'
import { Offer } from '../ui/Offer'
import { Failed, Waiting } from '../ui/Status'
import { Switch } from '../ui/Switch'
import { ClassPicker } from './connections/ClassPicker'
import { Folders } from './connections/Folders'
import { Sync } from './connections/Sync'
import { TimetableSource } from './connections/TimetableSource'

/**
 * How often the screen looks again while a sync is going on.
 *
 * A few seconds, and only while there is something to see: a sync of a semester's material takes
 * minutes, and a run has to be able to *end* on the screen without anybody reloading the page. Nothing
 * is asked at all once no run is going, because the answer would be the same one every time — and this
 * is a screen somebody leaves open on a phone.
 */
export const WHILE_A_SYNC_RUNS_MS = 4_000

/**
 * External systems: the ways MyLib is allowed to look into somebody else's installation.
 *
 * This is the only screen in MyLib that asks for a password, and everything about it follows from
 * that. **The promise stands beside the field**, in words rather than in a link to a policy: what the
 * password is used for, that it is never shown again, that it can be taken away, and what taking it
 * away does and does not undo. Somebody typing a university password into a private tool is entitled
 * to read that at the moment they type it, not afterwards.
 *
 * **A refusal is put where the wrong value is.** The service names three of them — the credentials,
 * the client, the address — and the whole reason it names three instead of one is so that this screen
 * can say *which field* to look at. One banner at the top saying "the connection test failed" would
 * throw that away and leave somebody trying values at random.
 *
 * Nothing is kept that has not been tried: saving *is* the connection test, so a connection that
 * appears in the list is one that worked at least once. That is why there is no separate "test it
 * first" control on the form, and why there is one on every connection in the list — the second
 * question is a different question, asked months later, and its answer is "does this still work".
 *
 * **A sync that is going on ends on this screen without a reload.** It runs in the service and takes
 * minutes, so the screen looks again every few seconds while one is running and stops the moment none
 * is — see {@link WHILE_A_SYNC_RUNS_MS}. What each connection says about its last run lives in
 * {@link Sync}, under the folders that decide what a run fetches.
 *
 * The area is reached at `#/connections`, from the account panel in the bar, and stands in no
 * navigation: setting up a connection is something somebody does once a semester.
 */
export function Connections() {
  const announce = useAnnounce()
  const access = useLoad(useCallback((signal: AbortSignal) => fetchExternalAccess(signal), []))

  /** Whether the form is open for a connection that does not exist yet. */
  const [adding, setAdding] = useState(false)
  /** Which connection is being corrected, if any. Held as an id, so a fresh list is the fresh values. */
  const [editingId, setEditingId] = useState<string | null>(null)
  /** Which connection has been asked about before being removed. Removing is the one thing that asks. */
  const [asking, setAsking] = useState<string | null>(null)
  /** Which connection has something on its way, so its controls stand still while it does. */
  const [working, setWorking] = useState<string | null>(null)
  /**
   * A refusal, and the connection it is about.
   *
   * Kept with its id so that it is drawn at that connection rather than at the top of the screen.
   * Feedback belongs on the object: with three connections in the list, a sentence at the top would
   * be a sentence about no particular one of them.
   */
  const [problem, setProblem] = useState<{ readonly id: string; readonly failure: Failure } | null>(
    null,
  )
  /**
   * The run this screen started itself, kept beside the list until the list has caught up with it.
   *
   * `POST …/sync` answers with the connection it started a run for, and that answer is the whole
   * reason the screen can say "syncing now" the moment somebody presses instead of a look later. It
   * is held apart from the loaded connection rather than written into it, because what the service
   * says is the truth and this is only ahead of it: as soon as a load brings a run that started at
   * the same moment or later, that one is shown and this one is never looked at again.
   */
  const [started, setStarted] = useState<Readonly<Record<string, SyncRun>>>({})

  const overview = access.load.status === 'ready' ? access.load.value : null
  const connections = overview?.connections ?? []
  const kinds = overview?.kinds ?? []
  const editing = connections.find((one) => one.id === editingId) ?? null
  const forming = adding || editing !== null

  /** Which run stands at a connection: the one the service last said, unless this screen has a newer. */
  const runAt = (connection: Connection): SyncRun | null => {
    const own = started[connection.id]
    const known = connection.lastRun
    if (own === undefined) {
      return known
    }
    return known !== null && moment(known.startedAt) >= moment(own.startedAt) ? known : own
  }

  /**
   * Which kind of system a connection reaches, as this build describes it.
   *
   * `null` for a connection whose connector is not in this build — a row set up by a version that
   * had one. It is drawn as itself with a line saying so, rather than being forced into one of the
   * two panels: guessing would show a folder browser for a timetable, which can never answer.
   */
  const systemOf = (connection: Connection): System | null =>
    kinds.find((one) => one.kind === connection.kind) ?? null

  const syncing = connections.some((connection) => runAt(connection)?.running === true)

  /*
    Looking again while something is running, and never otherwise.

    `retry` is taken out of the load on its own so that the timer depends on the function and not on
    the object it arrives in — an object built afresh every render would rebuild the timer every
    render, and a timer rebuilt every render never fires.
  */
  const lookAgain = access.retry
  useEffect(() => {
    if (!syncing) {
      return
    }
    const timer = setInterval(lookAgain, WHILE_A_SYNC_RUNS_MS)
    return () => {
      clearInterval(timer)
    }
  }, [syncing, lookAgain])

  const openForm = () => {
    setProblem(null)
    setEditingId(null)
    setAdding(true)
  }

  const openEdit = (connection: Connection) => {
    setProblem(null)
    setAdding(false)
    setEditingId(connection.id)
  }

  const closeForm = () => {
    setAdding(false)
    setEditingId(null)
  }

  /** Does something to one connection, and puts a refusal where that connection is. */
  const attempt = async (id: string, work: () => Promise<void>) => {
    setProblem(null)
    setWorking(id)
    try {
      await work()
    } catch (error) {
      setProblem({ id, failure: asFailure(error) })
    } finally {
      setWorking(null)
    }
  }

  const test = (connection: Connection) => {
    void attempt(connection.id, async () => {
      await testConnection(connection.id)
      access.retry()
      announce('That connection still works.')
    })
  }

  /**
   * Starts a run by hand, and shows it as running out of the answer rather than out of a later look.
   *
   * The service accepts the run and comes straight back with it, so there is nothing to wait for and
   * nothing to ask again for: what it answered is put beside the connection and the screen says
   * "syncing now" at once. The looking-again above then follows that run to its end.
   */
  const startSync = (connection: Connection) => {
    void attempt(connection.id, async () => {
      const run = (await sync(connection.id)).lastRun
      if (run !== null) {
        setStarted((before) => ({ ...before, [connection.id]: run }))
      }
      announce('Syncing now. It keeps going whether or not you stay on this screen.')
    })
  }

  const remove = (connection: Connection) => {
    setAsking(null)
    void attempt(connection.id, async () => {
      await removeConnection(connection.id)
      access.retry()
      if (connection.id === editingId) {
        closeForm()
      }
      announce('Removed. Everything taken over stays in your library.')
    })
  }

  return (
    <Lanes
      overview={{
        label: 'External systems',
        content: (
          <div className="connections">
            <header className="connections__head">
              <h2 className="connections__title">External systems</h2>
              {kinds.length > 0 && connections.length > 0 && (
                <Button tone="primary" onClick={openForm} disabled={forming}>
                  Connect another
                </Button>
              )}
            </header>

            {access.load.status === 'waiting' && <Waiting label="Looking up your connections." />}
            {access.load.status === 'failed' && (
              <Failed
                failure={access.load.failure}
                onRetry={access.retry}
                title="The connections did not load"
              />
            )}

            {overview !== null && (
              <>
                {connections.length > 0 && (
                  <ul className="connections__list">
                    {connections.map((connection) => (
                      <li className="connection" key={connection.id}>
                        <ConnectionFacts connection={connection} system={systemOf(connection)} />

                        {!connection.usable && (
                          <p className="connection__stale">
                            MyLib can no longer read the password kept for this connection — the key
                            this installation seals passwords with is not the one they were sealed
                            with. Nothing has been lost: type the password in again and it works as
                            before.
                          </p>
                        )}

                        {problem !== null && problem.id === connection.id && (
                          <Failed failure={problem.failure} />
                        )}

                        <div className="controls controls--tight">
                          {asking === connection.id ? (
                            <>
                              <p className="connection__asking">
                                Remove the connection to {connection.address}? The syncing stops
                                there and then. Everything already taken over stays in your library
                                — openable, downloadable and findable exactly as it is now.
                              </p>
                              <Button
                                tone="quiet"
                                className="control--danger"
                                onClick={() => {
                                  remove(connection)
                                }}
                              >
                                Remove it
                              </Button>
                              <Button
                                tone="bare"
                                onClick={() => {
                                  setAsking(null)
                                }}
                              >
                                Keep it
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                tone="quiet"
                                aria-label={`Test the connection to ${connection.address}`}
                                /*
                                  A password nobody can open cannot be tried, so the control that
                                  would try it says so by standing down. The line above says why.
                                */
                                disabled={!connection.usable || working === connection.id}
                                onClick={() => {
                                  test(connection)
                                }}
                              >
                                Test
                              </Button>
                              <Button
                                tone="quiet"
                                aria-label={`Change the connection to ${connection.address}`}
                                disabled={working === connection.id}
                                onClick={() => {
                                  openEdit(connection)
                                }}
                              >
                                {connection.usable ? 'Edit' : 'Enter the password again'}
                              </Button>
                              <Button
                                tone="bare"
                                className="control--danger"
                                aria-label={`Remove the connection to ${connection.address}`}
                                disabled={working === connection.id}
                                onClick={() => {
                                  setAsking(connection.id)
                                }}
                              >
                                Remove
                              </Button>
                            </>
                          )}
                        </div>

                        {/*
                          What comes over from this system, on the connection itself.

                          It belongs here and not on a screen of its own: a folder ticked over there
                          means nothing without the account it is ticked with, and somebody who has
                          just set a connection up is exactly the person who has to choose. What is
                          ticked is a read of MyLib's own database and is therefore always shown; the
                          walk through the foreign tree costs that system a request per folder, so it
                          waits until it is asked for.
                        */}
                        {systemOf(connection)?.brings === 'FILES' && (
                          <Folders
                            connectionId={connection.id}
                            address={connection.address}
                            usable={connection.usable}
                          />
                        )}

                        {/*
                          The same place on the screen, for a system that brings a timetable: there is
                          no tree to tick, so what stands here is the period, whose plan, and whether a
                          double period is one appointment. See {@link TimetableSource}.
                        */}
                        {systemOf(connection)?.brings === 'TIMETABLE' && (
                          <TimetableSource
                            connectionId={connection.id}
                            systemName={systemOf(connection)?.systemName ?? connection.kind}
                            usable={connection.usable}
                          />
                        )}

                        {systemOf(connection) === null && (
                          <p className="connection__stale">
                            This build of MyLib has no connector for {connection.kind} any more, so
                            nothing can be fetched from it. The connection and everything it brought
                            over stay exactly as they are.
                          </p>
                        )}

                        {/*
                          When it last looked over there, what it found, and the control that looks
                          now. Under the folders, because what a sync fetches is exactly what is
                          ticked above it.
                        */}
                        <Sync
                          kind={systemOf(connection)?.systemName ?? connection.kind}
                          brings={systemOf(connection)?.brings ?? 'FILES'}
                          address={connection.address}
                          run={runAt(connection)}
                          usable={connection.usable}
                          busy={working === connection.id}
                          onSyncNow={() => {
                            startSync(connection)
                          }}
                          onEnterPasswordAgain={() => {
                            openEdit(connection)
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                )}

                {connections.length === 0 && !forming && (
                  <Offer
                    title="Bring your course material in"
                    lead="Your lecture slides, scripts and exercise sheets live in your university's system. Set that system up here once, and MyLib fetches them into your library — new files as the semester goes on, without you downloading and uploading anything."
                    points={[
                      'You choose which folders are taken over; single files are never picked, so what arrives later arrives too.',
                      'What comes over behaves like anything else in your library: readable, downloadable, and findable by the text inside it.',
                      'Your password is used to sign in as you and read, and for nothing else.',
                    ]}
                    action={
                      kinds.length > 0 ? (
                        <Button tone="primary" onClick={openForm}>
                          Connect a system
                        </Button>
                      ) : undefined
                    }
                    note={
                      kinds.length === 0
                        ? 'This build of MyLib has no connector for any system yet, so there is nothing to connect to.'
                        : undefined
                    }
                  />
                )}

                {forming && (
                  <ConnectionForm
                    // A different connection is a different set of values, not the same fields with
                    // other words in them — remounting is what seeds them.
                    key={editing?.id ?? 'new'}
                    kinds={kinds}
                    connection={editing}
                    onSaved={() => {
                      closeForm()
                      access.retry()
                      announce(editing === null ? 'Connected.' : 'Changed.')
                    }}
                    onCancel={closeForm}
                  />
                )}
              </>
            )}
          </div>
        ),
      }}
    />
  )
}

/**
 * What this installation cannot do, said plainly.
 *
 * It names the setting on purpose. Whoever reads this either runs the installation themselves or is
 * about to ask the person who does, and "external systems are not available" without the name of the
 * thing that would make them available is a dead end for both of them.
 */
/**
 * One connection, as it says what it is: where it goes, and who it goes as.
 *
 * What the last run did is deliberately not here. These are the values somebody typed, and they are
 * facts with names — "client", "signed in as". A sync is not: it is something that happened, and it is
 * said in sentences under the folders it fetched, in {@link Sync}.
 */
function ConnectionFacts({
  connection,
  system,
}: {
  readonly connection: Connection
  readonly system: System | null
}) {
  return (
    <div className="connection__what">
      {/* What the system calls itself and not the name of an enum: `WebUntis`, never `WEB_UNTIS`. */}
      <h3 className="connection__kind">{system?.systemName ?? connection.kind}</h3>
      <p className="connection__address">{connection.address}</p>

      <dl className="facts">
        <div className="facts__row">
          <dt className="facts__key">{tenantIsCalled(system)}</dt>
          <dd className="facts__value">{connection.tenant === '' ? 'None' : connection.tenant}</dd>
        </div>
        <div className="facts__row">
          <dt className="facts__key">Signed in as</dt>
          <dd className="facts__value">{connection.username}</dd>
        </div>
        <div className="facts__row">
          <dt className="facts__key">Password</dt>
          <dd className="facts__value">
            {connection.usable ? 'Kept, and never shown again' : 'Cannot be read any more'}
          </dd>
        </div>
      </dl>
    </div>
  )
}

/**
 * What the tenant field is called at this kind of system.
 *
 * Derived from what the system **brings** and deliberately not from its name: a timetable system is
 * asked which school, a file system which client, and a second timetable system inherits the right
 * word without this file learning that it exists. A build with no connector for a row falls back to
 * the general word, which is what the field has always been called.
 */
function tenantIsCalled(system: System | null): string {
  return system?.brings === 'TIMETABLE' ? 'School' : 'Client'
}

/**
 * The form: five values, and the promise that goes with the fifth.
 *
 * It is one form for setting up and for correcting, because they differ in exactly one thing — an
 * existing connection keeps its password when the field is left empty, which is what makes a mistyped
 * user name correctable by somebody who was promised never to see that password again.
 */
function ConnectionForm({
  kinds,
  connection,
  onSaved,
  onCancel,
}: {
  readonly kinds: readonly System[]
  /** The connection being corrected, or `null` for one that does not exist yet. */
  readonly connection: Connection | null
  readonly onSaved: (saved: Connection) => void
  readonly onCancel: () => void
}) {
  const base = useId()
  const [kind, setKind] = useState(connection?.kind ?? kinds[0]?.kind ?? '')
  const [address, setAddress] = useState(connection?.address ?? '')
  const [tenant, setTenant] = useState(connection?.tenant ?? '')
  const [username, setUsername] = useState(connection?.username ?? '')
  const [password, setPassword] = useState('')
  const [saving, setSaving] = useState(false)
  const [failure, setFailure] = useState<Failure | null>(null)

  /*
    What a timetable connection brings, asked here rather than after it exists.

    A connection that is saved and then has to be completed at a second screen is a connection that
    stays half set up: whoever pressed Connect has arrived, and the class — the one setting that
    decides whether anything comes over at all — is somewhere behind a control they have no reason
    to look for. So every setting is on this form, the class included, and the one press writes both.
  */
  const [from, setFrom] = useState(aYearBack)
  const [merge, setMerge] = useState(true)
  const [element, setElement] = useState<TimetableElement | null>(null)
  /*
    The credentials as they stood when the classes were asked for, and `null` while nobody has.

    Held apart from the fields on purpose: a load that depended on the fields themselves would sign
    in over there again on every keystroke in the password, which is somebody else's server and, at
    WebUntis, one login attempt per character.
  */
  const [probe, setProbe] = useState<NewConnection | null>(null)

  const said = failure === null ? null : failure.message
  const at = failure === null ? null : fieldRefused(failure)
  const problemAt = (field: ConnectionField): string | null => (at === field ? said : null)
  /** What describes a field: whatever it always says, plus the refusal when there is one. */
  const describing = (field: ConnectionField, ...rest: (string | null)[]): string | undefined => {
    const parts = [...rest, at === field ? `${base}-${field}-problem` : null].filter(
      (part) => part !== null && part !== '',
    )
    return parts.length === 0 ? undefined : parts.join(' ')
  }

  /**
   * Fills the client in once the address is complete, and never over anything somebody typed.
   *
   * A suggestion and not an answer: what settles the value is the connection test with the real
   * credentials. So this cannot fail, cannot say anything, and cannot overwrite — the answer arrives
   * whenever it arrives, and by then the field may no longer be empty.
   */
  const suggest = () => {
    const wanted = address.trim()
    if (kind === '' || wanted === '' || tenant.trim() !== '') {
      return
    }
    void suggestedTenant(kind, wanted).then((found) => {
      if (found !== null) {
        setTenant((was) => (was.trim() === '' ? found : was))
      }
    })
  }

  /** The kind being set up, as this build describes it — what decides the words on two fields. */
  const chosenSystem = kinds.find((one) => one.kind === kind) ?? null

  const whose = useLoad(
    useCallback(
      async (signal: AbortSignal) =>
        probe === null ? null : await whoseTimetableFor(probe, signal),
      [probe],
    ),
  )
  const person = whose.load.status === 'ready' ? (whose.load.value?.person ?? null) : null
  const choices = whose.load.status === 'ready' ? (whose.load.value?.choices ?? []) : []
  /*
    Whether the other system is being asked *right now*.

    `whose.load.status` alone will not do: a load that has never been asked for anything is
    `waiting` too — it is the state the hook starts in — and a control disabled by that is a control
    that is dead on arrival for exactly as long as nobody has pressed it.
  */
  const asking = probe !== null && whose.load.status === 'waiting'

  /** Whether this form is also choosing what comes over. Only ever for a new timetable connection. */
  const alsoChoosing = connection === null && chosenSystem?.brings === 'TIMETABLE'

  const values: ChangedConnection = {
    address: address.trim(),
    tenant: tenant.trim(),
    username: username.trim(),
    // Empty means "keep the one that is there", which only an existing connection can mean.
    password: password === '' ? null : password,
  }

  const ready =
    kind !== '' &&
    values.address !== '' &&
    values.username !== '' &&
    (connection !== null || password !== '')

  const submit = () => {
    setSaving(true)
    setFailure(null)
    // Saving *is* the connection test — the service signs in over there before it keeps anything —
    // so what comes back from this is either a connection that works or the name of a wrong value.
    const trying =
      connection === null
        ? addConnection({ ...values, kind, password }).then(async (made) => {
            // The second write, and it is not a second step for whoever pressed the button: what
            // comes over was already answered above, so a connection never exists in a state where
            // nobody has said what it is for. If this half fails the connection stands and the panel
            // below it asks the same three things again — nothing is lost, and nothing is silent.
            if (alsoChoosing) {
              await chooseTimetable(made.id, {
                from,
                elementKind: element?.kind ?? null,
                elementId: element?.id ?? null,
                elementName: element?.name ?? '',
                mergeAdjacent: merge,
              })
            }
            return made
          })
        : changeConnection(connection.id, values)

    trying
      .then((result) => {
        // Out of this side the moment it is no longer needed. What was typed lives on in one place
        // only — sealed beside the row in the database — and there is no way back out of there.
        setPassword('')
        onSaved(result)
      })
      .catch((error: unknown) => {
        setFailure(asFailure(error))
        setSaving(false)
      })
  }

  return (
    <form
      className="connection-form"
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <h3 className="connection-form__title">
        {connection === null ? 'Connect a system' : `Change ${connection.address}`}
      </h3>

      {/* Only when it is about none of the fields. Anything that names one is said at that one. */}
      {failure !== null && at === null && <Failed failure={failure} />}

      {connection === null && (
        <Field id={`${base}-kind`} label="System" problem={problemAt('kind')}>
          <select
            id={`${base}-kind`}
            className="field__control"
            value={kind}
            aria-invalid={at === 'kind'}
            aria-describedby={describing('kind')}
            onChange={(event) => {
              setKind(event.target.value)
            }}
          >
            {kinds.map((one) => (
              <option key={one.kind} value={one.kind}>
                {one.systemName}
              </option>
            ))}
          </select>
        </Field>
      )}

      <Field
        id={`${base}-address`}
        label="Address"
        hint={
          chosenSystem?.brings === 'TIMETABLE'
            ? 'Where that system answers, for example https://herakles.webuntis.com'
            : 'Where that system answers, for example https://ilias.example.edu'
        }
        problem={problemAt('address')}
      >
        <input
          id={`${base}-address`}
          className="field__control"
          type="url"
          inputMode="url"
          autoComplete="url"
          spellCheck={false}
          value={address}
          aria-invalid={at === 'address'}
          aria-describedby={describing('address', `${base}-address-hint`)}
          onChange={(event) => {
            setAddress(event.target.value)
          }}
          // On leaving the field rather than on every keystroke: half an address is not one, and
          // asking about it would be a request per character to somebody else's server.
          onBlur={suggest}
        />
      </Field>

      <Field
        id={`${base}-tenant`}
        label={tenantIsCalled(chosenSystem)}
        hint={
          chosenSystem?.brings === 'TIMETABLE'
            ? 'Which school on that server. MyLib fills it in from the address, so you should not have to know it.'
            : 'Which client of that installation. MyLib suggests one once the address is in; leave it empty for a system that has no clients.'
        }
        problem={problemAt('tenant')}
      >
        <input
          id={`${base}-tenant`}
          className="field__control"
          spellCheck={false}
          value={tenant}
          aria-invalid={at === 'tenant'}
          aria-describedby={describing('tenant', `${base}-tenant-hint`)}
          onChange={(event) => {
            setTenant(event.target.value)
          }}
        />
      </Field>

      <Field
        id={`${base}-username`}
        label="User name"
        hint="Who you are over there, not who you are here."
        problem={problemAt('username')}
      >
        <input
          id={`${base}-username`}
          className="field__control"
          autoComplete="username"
          spellCheck={false}
          value={username}
          aria-invalid={at === 'username'}
          aria-describedby={describing('username', `${base}-username-hint`)}
          onChange={(event) => {
            setUsername(event.target.value)
          }}
        />
      </Field>

      <Field
        id={`${base}-password`}
        label="Password"
        hint={
          connection === null
            ? undefined
            : 'Leave this empty to keep the password that is already there.'
        }
        problem={problemAt('password')}
      >
        <input
          id={`${base}-password`}
          className="field__control"
          type="password"
          /*
            Deliberately not `current-password`: this is somebody else's password being handed to
            MyLib, not the one they sign in here with, and a browser offering to fill in the MyLib
            password would be offering the wrong secret to the wrong system.
          */
          autoComplete="new-password"
          value={password}
          aria-invalid={at === 'password'}
          aria-describedby={describing(
            'password',
            `${base}-promise`,
            connection === null ? null : `${base}-password-hint`,
          )}
          onChange={(event) => {
            setPassword(event.target.value)
          }}
        />
      </Field>

      {/*
        The promise, where the password is typed and not in a policy somewhere.

        Four sentences, and each of them is a decision made elsewhere in MyLib rather than a
        reassurance written here: the connector only ever reads, the port has no method that answers
        with a password, removing a connection is one control away, and a sync deletes nothing.
      */}
      <div className="promise" id={`${base}-promise`}>
        <p className="promise__lead">What MyLib does with this password:</p>
        <ul className="promise__points">
          <li>It is used only to sign in as you and read. Nothing is ever written back.</li>
          <li>It is never shown to you again — not here, not anywhere.</li>
          <li>You can remove it at any time, by removing the connection.</li>
          <li>
            Removing it ends the syncing and nothing else: everything already taken over stays in
            your library.
          </li>
        </ul>
      </div>

      {alsoChoosing && (
        <fieldset className="connection-form__part">
          <legend className="connection-form__part-title">What comes over</legend>
          <p className="connection-form__part-lead">
            {chosenSystem?.systemName} brings a timetable rather than folders. Everything from the
            day below is taken over and kept up to date, as far ahead as it publishes it.
          </p>

          <Field
            id={`${base}-from`}
            label="Take over everything from"
            hint="A year back by default, which is the whole of last term. There is no end to set: how far the plan reaches is asked afresh on every sync."
            problem={null}
          >
            <input
              id={`${base}-from`}
              className="field__control field__control--narrow"
              type="date"
              value={from}
              onChange={(event) => {
                setFrom(event.target.value)
              }}
            />
          </Field>

          <div className="connection-form__setting">
            <Switch
              label="Merge double periods into one appointment"
              hint={`${chosenSystem?.systemName ?? 'The system'} sends a double period as one entry per period. Merged, two lessons that touch become a single appointment in your timetable.`}
              on={merge}
              onChange={setMerge}
            />
          </div>

          <div className="connection-form__setting">
            <p className="field__label">Whose plan</p>
            {element === null ? (
              <p className="source__aside">
                Nothing chosen yet, so MyLib would fetch the plan of whoever these credentials are.
                At a shared login that is nobody in particular — then a class has to be chosen.
              </p>
            ) : (
              <p className="source__chosen">
                <strong>{element.name}</strong>
                <Button
                  tone="bare"
                  onClick={() => {
                    setElement(null)
                  }}
                >
                  Choose a different class
                </Button>
              </p>
            )}

            {/*
              The classes cannot be listed without signing in over there, so this control is also
              the connection test — and it is pressed before anything is saved, which is the whole
              point of the section: the class is chosen on the form like every other setting.
            */}
            <div className="controls controls--tight">
              <Button
                tone="quiet"
                disabled={!ready || asking}
                onClick={() => {
                  setProbe({ ...values, kind, password })
                }}
              >
                {choices.length === 0
                  ? `Show the classes at ${chosenSystem?.systemName ?? 'that system'}`
                  : 'Ask again'}
              </Button>
            </div>

            {asking && (
              <Waiting
                label={`Asking ${chosenSystem?.systemName ?? 'that system'} whose plan it can give.`}
              />
            )}
            {whose.load.status === 'failed' && (
              <Failed
                failure={whose.load.failure}
                onRetry={whose.retry}
                title="That did not work"
              />
            )}
            {whose.load.status === 'ready' && whose.load.value !== null && person !== null && (
              <p className="source__aside">
                This account is <strong>{person.name}</strong>, so their own plan is what comes
                over. There is nothing to choose.
              </p>
            )}
            {whose.load.status === 'ready' && whose.load.value !== null && person === null && (
              <ClassPicker choices={choices} chosen={element} onChoose={setElement} />
            )}
          </div>
        </fieldset>
      )}

      <div className="controls">
        <Button tone="primary" type="submit" disabled={!ready || saving}>
          {saving ? 'Trying it…' : connection === null ? 'Connect' : 'Save'}
        </Button>
        <Button tone="bare" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        {/*
          Said before it happens, because it takes seconds and because it explains why "Connect" is
          the same control as "test": nothing is kept here that has not been tried.
        */}
        <p className="connection-form__note">
          MyLib signs in over there before it keeps anything. Nothing is saved unless it works.
        </p>
      </div>
    </form>
  )
}

/**
 * A year ago today, as `YYYY-MM-DD` — what a timetable connection takes over from by default.
 *
 * A whole term behind whoever is setting it up, so their last semester is there rather than
 * beginning at the day they happened to connect. Built out of the parts and never out of a
 * moment: these are days on a calendar, exactly as the service stores them.
 */
function aYearBack(): string {
  const now = new Date()
  return [
    now.getFullYear() - 1,
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-')
}

/** One value on the form, its label, what it is for, and what was wrong with it. */
function Field({
  id,
  label,
  hint,
  problem,
  children,
}: {
  readonly id: string
  readonly label: string
  readonly hint?: string
  /** The sentence the service refused this value with, or `null` while there is nothing wrong. */
  readonly problem: string | null
  readonly children: ReactNode
}) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children}
      {hint !== undefined && (
        <p className="field__hint" id={`${id}-hint`}>
          {hint}
        </p>
      )}
      {problem !== null && (
        <p className="field__problem" id={`${id}-problem`} role="alert">
          {problem}
        </p>
      )}
    </div>
  )
}

/**
 * A moment as a number, for deciding which of two runs is the later one.
 *
 * Compared as instants and never as text: the service writes an instant with as many fractional
 * digits as it happens to have, and `…:00Z` sorts after `…:00.123Z` as a string. An unreadable moment
 * counts as the beginning of time, so the run that can be read wins.
 */
function moment(iso: string): number {
  const at = Date.parse(iso)
  return Number.isNaN(at) ? 0 : at
}
